import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Alert, Typography, Button } from '@mui/material';
import { useAuthContext } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  fallback?: ReactNode;
  redirectOnForbidden?: boolean;  // New prop to control redirect behavior
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  fallback,
  redirectOnForbidden = false  // Default to false for backwards compatibility
}) => {
  const { user, isAuthenticated, hasPermission, loginWithAzure } = useAuthContext();

  // Not authenticated at all
  if (!isAuthenticated || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Authentication Required
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You need to sign in to access this area of the application.
          </Typography>
          <Button variant="contained" onClick={loginWithAzure}>
            Sign In with Microsoft
          </Button>
        </Alert>
      </Box>
    );
  }

  // Authenticated but insufficient permissions
  if (requiredRole && !hasPermission(requiredRole)) {
    // If redirect enabled and user is a student, redirect to itinerary
    if (redirectOnForbidden && user.roles?.includes('student')) {
      return <Navigate to="/itinerary" replace />;
    }
    
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You need at least <strong>{requiredRole}</strong> role to access this feature.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your current roles: {user.roles.join(', ') || 'None'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Contact your administrator if you need additional access.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // All checks passed
  return <>{children}</>;
};

export default ProtectedRoute;
